import { useMemo } from "react";
import { Marked, marked } from "marked";
//#region src/stream-autoclose/stream-autoclose.ts
/**
* Initialize streaming parser state.
* @returns A fresh StreamState for incremental markdown parsing.
*/
function initState() {
	return {
		open: [],
		lastContent: "",
		codeDepth: 0,
		inLinkUrl: false
	};
}
/** Set of single-character emphasis delimiters for quick lookup */
const EMPHASIS_DELIMS = /* @__PURE__ */ new Set(["_", "*"]);
/** Set of double-character delimiters for quick lookup */
const DOUBLE_DELIMS = /* @__PURE__ */ new Set([
	"__",
	"**",
	"~~"
]);
/** Set of opening bracket delimiters for quick lookup */
const BRACKET_DELIMS = /* @__PURE__ */ new Set(["[", "("]);
/** Map from closing brackets to their corresponding opening brackets */
const CLOSING_BRACKETS = /* @__PURE__ */ new Map([["]", "["], [")", "("]]);
/** Map from opening delimiters to their corresponding closing delimiters */
const DELIMITER_CLOSERS = {
	_: "_",
	"*": "*",
	__: "__",
	"**": "**",
	"~~": "~~",
	"[": "]",
	"(": ")"
};
/** Character codes for list bullet detection */
const ASTERISK = 42;
const MINUS = 45;
const PLUS = 43;
const LF = 10;
/** Pre-compiled regex for math expression detection */
const NUMBER_CHAR_REGEX = /^[\d.]$/;
/** Pre-compiled regex for detecting previous list content in nested contexts */
const PREVIOUS_LIST_REGEX = /(?:^|\n)[ \t]*(?:\d+\.|[*+-])[ \t]/;
/**
* Check if a delimiter is a backtick sequence (code delimiter)
* @param d - The delimiter to check
* @returns True if the delimiter is a backtick sequence
*/
function isCodeDelim(d) {
	return typeof d === "string" && d.startsWith("`");
}
/**
* Toggle a delimiter in the parser state - either close existing or open new
* Maintains codeDepth counter in O(1) time
* @param state - The current parser state
* @param delim - The delimiter to toggle
*/
function toggleDelimiter(state, delim) {
	const idx = state.open.lastIndexOf(delim);
	if (idx !== -1) {
		state.open.splice(idx, 1);
		if (isCodeDelim(delim)) state.codeDepth--;
	} else {
		state.open.push(delim);
		if (isCodeDelim(delim)) state.codeDepth++;
	}
}
/** Detect `*`, `-`, `+` list bullets
* Supports LF & CRLF
*/
function isListBullet(i, text) {
	const ch = text.charCodeAt(i);
	if (ch !== ASTERISK && ch !== MINUS && ch !== PLUS) return false;
	const next = text.charCodeAt(i + 1);
	if (next !== 32 && next !== 9) return false;
	const maxDist = 8;
	for (let dist = 1; dist <= maxDist && i - dist >= 0; dist++) {
		const c = text.charCodeAt(i - dist);
		if (c === LF) {
			const beforeNewline = text.slice(0, i - dist);
			if (PREVIOUS_LIST_REGEX.test(beforeNewline) && dist <= 8) return true;
			return dist <= 8;
		}
		if (c === 13) return text.charCodeAt(i - dist + 1) === LF;
		if (c !== 32 && c !== 9) break;
	}
	for (let j = i - 1; j >= 0 && j >= i - 4; j--) {
		const c = text.charCodeAt(j);
		if (c !== 32 && c !== 9) return false;
	}
	return i <= 4;
}
/**
* Detect list bullets that might be at the end of content (without following whitespace)
* More lenient than isListBullet for streaming scenarios - allows deeper indentation
*/
function isPotentialListBullet(i, text) {
	const ch = text.charCodeAt(i);
	if (ch !== ASTERISK && ch !== MINUS && ch !== PLUS) return false;
	for (let dist = 1; dist <= 8 && i - dist >= 0; dist++) {
		const c = text.charCodeAt(i - dist);
		if (c === LF) {
			const beforeNewline = text.slice(0, i - dist);
			if (PREVIOUS_LIST_REGEX.test(beforeNewline) && dist <= 8) return true;
			return dist <= 4;
		}
		if (c === 13) return text.charCodeAt(i - dist + 1) === LF;
		if (c !== 32 && c !== 9) break;
	}
	for (let j = i - 1; j >= 0 && j >= i - 4; j--) {
		const c = text.charCodeAt(j);
		if (c !== 32 && c !== 9) return false;
	}
	return i > 0 && i <= 4;
}
/**
* Parse a sequence of backticks starting at the given position
* @param pos - Starting position in the text
* @param text - The full text content
* @returns Object containing the backtick delimiter string and its length
*/
function backtickSeq(pos, text) {
	let len = 1;
	while (pos + len < text.length && text.charCodeAt(pos + len) === 96) len++;
	return {
		delim: "`".repeat(len),
		len
	};
}
/**
* Check if asterisk at position i is part of a simple math expression (number * number)
* @param i - Position of the asterisk in the text
* @param text - The full text content
* @returns True if this appears to be a simple math expression
*/
function isSimpleMathExpression(i, text) {
	const beforePos = i - 1;
	const beforeChar = beforePos >= 0 ? text[beforePos] : "";
	const afterPos = i + 1;
	const afterChar = afterPos < text.length ? text[afterPos] : "";
	return NUMBER_CHAR_REGEX.test(beforeChar) && NUMBER_CHAR_REGEX.test(afterChar);
}
/**
* Check if double asterisk at position i is part of an exponentiation expression (number**number)
* Only matches tight expressions without spaces to avoid conflicts with markdown bold formatting.
* @param i - Position of the first asterisk in the '**' sequence
* @param text - The full text content
* @returns True if this appears to be a tight exponentiation expression
*
* @example
* isExponentiationExpression(1, "2**3") // → true
* isExponentiationExpression(1, ".5**2.1") // → true
* isExponentiationExpression(2, "2 **3") // → false (spaces not allowed)
* isExponentiationExpression(2, "x**y") // → false (not numbers)
*/
function isExponentiationExpression(i, text) {
	if (i + 1 >= text.length || text.slice(i, i + 2) !== "**") return false;
	const beforePos = i - 1;
	const beforeChar = beforePos >= 0 ? text[beforePos] : "";
	const afterPos = i + 2;
	const afterChar = afterPos < text.length ? text[afterPos] : "";
	return NUMBER_CHAR_REGEX.test(beforeChar) && NUMBER_CHAR_REGEX.test(afterChar);
}
/**
* Process streaming markdown content and auto-close unmatched delimiters
*
* This function performs incremental parsing, only processing new content since
* the last call. It maintains a stack of unmatched delimiters and automatically
* closes them at the end of the content to produce valid markdown.
*
* @param content - The current markdown content (may include previous content)
* @param state - The parser state from previous calls (maintains delimiter stack)
* @param options - Configuration options for auto-closing behavior
* @returns The content with auto-closed delimiters appended
*
* @example
* ```typescript
* const state = initState();
* const result1 = autoCloseStream("Hello **bold", state);
* // Returns: "Hello **bold**"
*
* const result2 = autoCloseStream("Hello **bold text", state);
* // Returns: "Hello **bold text**" (incremental parsing)
* ```
*/
function autoCloseStream(content, state, options = {}) {
	const { resetState = false, autoCloseInlineCode = true, autoCloseCodeBlocks = false, supportMathExpressions = true, enablePostProcessing = true } = options;
	let start = 0;
	let out = "";
	if (resetState || !content.startsWith(state.lastContent)) {
		state.open.length = 0;
		state.codeDepth = 0;
		state.inLinkUrl = false;
		state.pendingDelimiter = void 0;
	} else {
		start = state.lastContent.length;
		out = state.lastContent;
	}
	state.lastContent = content;
	if (state.pendingDelimiter && start < content.length) state.pendingDelimiter = void 0;
	let i = start;
	let inCode = state.codeDepth > 0;
	while (i < content.length) {
		const ch = content.charCodeAt(i);
		if (ch === 92) {
			out += i + 1 < content.length ? content.slice(i, i + 2) : "\\";
			i += 2;
			continue;
		}
		if (ch === 96) {
			const { delim, len } = backtickSeq(i, content);
			toggleDelimiter(state, delim);
			inCode = state.codeDepth > 0;
			out += delim;
			i += len;
			continue;
		}
		if (inCode) {
			out += content[i];
			i++;
			continue;
		}
		if (i + 1 < content.length && !state.inLinkUrl) {
			const pair = content.slice(i, i + 2);
			if (DOUBLE_DELIMS.has(pair)) {
				if ((pair === "**" || pair === "__") && isListBullet(i - 1, content)) {
					out += pair;
					i += 2;
					continue;
				}
				if (supportMathExpressions && pair === "**" && isExponentiationExpression(i, content)) {
					out += pair;
					i += 2;
					continue;
				}
				if (supportMathExpressions && pair === "**") {
					const beforePos = i - 1;
					const beforeChar = beforePos >= 0 ? content[beforePos] : "";
					if (NUMBER_CHAR_REGEX.test(beforeChar) && !state.open.some((d) => d === "**")) {
						out += pair;
						i += 2;
						continue;
					}
				}
				toggleDelimiter(state, pair);
				out += pair;
				i += 2;
				continue;
			}
		}
		if (EMPHASIS_DELIMS.has(content[i]) && !state.inLinkUrl) {
			if (isListBullet(i, content)) {
				out += content[i];
				i++;
				continue;
			}
			if (content[i] === "*" && i === content.length - 1 && isPotentialListBullet(i, content)) {
				state.pendingDelimiter = {
					char: "*",
					position: i,
					context: "afterBullet"
				};
				out += "*";
				i++;
				continue;
			}
			if (supportMathExpressions && content[i] === "*" && isSimpleMathExpression(i, content)) {
				out += content[i];
				i++;
				continue;
			}
			if (supportMathExpressions && content[i] === "*") {
				const beforePos = i - 1;
				const beforeChar = beforePos >= 0 ? content[beforePos] : "";
				const hasOpenDelimiter = state.open.some((d) => d === "*" || d === "**");
				const followsAsterisk = beforePos > 0 && content[beforePos - 1] === "*";
				if (NUMBER_CHAR_REGEX.test(beforeChar) && !hasOpenDelimiter && !followsAsterisk) {
					out += content[i];
					i++;
					continue;
				}
			}
			if (content[i] === "*") {
				const beforePos = i - 1;
				const afterPos = i + 1;
				const beforeChar = beforePos >= 0 ? content[beforePos] : "";
				const afterChar = afterPos < content.length ? content[afterPos] : "";
				if ((beforeChar === " " || beforeChar === "	" || beforeChar === "\n") && (afterChar === " " || afterChar === "	" || afterChar === "\n")) {
					let hasContentBefore = false;
					for (let j = beforePos - 1; j >= 0; j--) {
						const ch = content[j];
						if (ch === "\n") break;
						if (ch !== " " && ch !== "	") {
							hasContentBefore = true;
							break;
						}
					}
					if (hasContentBefore) {
						out += content[i];
						i++;
						continue;
					}
				}
			}
			toggleDelimiter(state, content[i]);
			out += content[i];
			i++;
			continue;
		}
		if (BRACKET_DELIMS.has(content[i])) {
			state.open.push(content[i]);
			out += content[i];
			i++;
			continue;
		}
		const opener = CLOSING_BRACKETS.get(content[i]);
		if (opener) {
			const idx = state.open.lastIndexOf(opener);
			if (idx !== -1) {
				state.open.splice(idx, 1);
				if (isCodeDelim(opener)) state.codeDepth--;
				if (opener === "[" && i + 1 < content.length && content[i + 1] === "(") state.inLinkUrl = true;
				if (opener === "(" && state.inLinkUrl) state.inLinkUrl = false;
			}
			out += content[i];
			i++;
			continue;
		}
		out += content[i];
		i++;
	}
	if (!state.pendingDelimiter) for (let j = state.open.length - 1; j >= 0; j--) {
		const d = state.open[j];
		const isCode = isCodeDelim(d), isBlock = isCode && d.length >= 3;
		if (isCode && !autoCloseInlineCode || isBlock && !autoCloseCodeBlocks) continue;
		if (EMPHASIS_DELIMS.has(d) || DOUBLE_DELIMS.has(d)) out = out.trimEnd();
		out += DELIMITER_CLOSERS[d] ?? d;
		state.open.pop();
		if (isCode) state.codeDepth--;
	}
	return enablePostProcessing ? postProcess(out) : out;
}
/**
* Apply all post-processing transformations to the output
* @param content - The processed markdown content
* @returns Content with post-processing applied
*/
function postProcess(content) {
	content = completeTrailingLinks(content);
	content = appendImageSyntaxIfTrailingExclamation(content);
	content = removeTrailingDoubleBackticks(content);
	content = stripTrailingDelims(content);
	content = completeHorizontalRule(content);
	return content;
}
/** Pre-compiled regex for trailing link completion with optional emphasis markers */
const TRAILING_LINK_REGEX = /(\(?)(\[([^\]]*)\])(?!\()(\)?)((\*{1,3}|_{1,3})?)$/;
/**
* Converts trailing bracket patterns to markdown link format with optional emphasis.
*
* Matches `[text]` patterns at the end of strings (optionally followed by emphasis markers)
* and converts them to `[text]()` format. Also handles parentheses-wrapped brackets like
* `([text])` which become `([text]())`. Handles emphasis markers like `*`, `**`, `***`,
* `_`, `__`, `___` by preserving them after the link parentheses.
*
* @param s - The input string to process
* @returns The string with link parentheses added to trailing bracket patterns
*
* @example
* completeTrailingLinks("Hello [world]"); // → "Hello [world]()"
* @example
* completeTrailingLinks("Z ([A])"); // → "Z ([A]())"
* @example
* completeTrailingLinks("Test ([])"); // → "Test ([]())"
* @example
* completeTrailingLinks("See [link]*"); // → "See [link]()*"
* @example
* completeTrailingLinks("Check [this]**"); // → "Check [this]()**"
* @example
* completeTrailingLinks("Read [docs]___"); // → "Read [docs]()___"
* @example
* completeTrailingLinks("Already [done](url)"); // → "Already [done](url)" (no change)
* @example
* completeTrailingLinks("Middle [text] here"); // → "Middle [text] here" (no change)
*
* @remarks
* Uses regex pattern to match bracket pairs with optional trailing emphasis markers.
* Only processes patterns at the end of the string to avoid modifying inline bracketed text.
* Supports both standalone brackets `[text]` and parentheses-wrapped brackets `([text])`.
*/
function completeTrailingLinks(s) {
	s = s.replace(TRAILING_LINK_REGEX, "$1$2()$4$5");
	return s;
}
/**
* Convert trailing exclamation mark with whitespace to image syntax
* @param s - Input string
* @returns String with ![() appended if ends with ! + whitespace
*/
function appendImageSyntaxIfTrailingExclamation(s) {
	if (s.length >= 2 && s[s.length - 1] === "!" && s[s.length - 2] === " ") return s.replace(/!\s*$/, "![]()");
	return s;
}
/**
* Remove malformed nested bullet list patterns from markdown content.
*
* Cleans up incomplete nested list items that can occur during streaming:
* - Empty nested bullet lists: `\n    *` or `\n    -`
* - Nested bullets with empty italic emphasis: `\n    *   **` or `\n    *   __`
* - Nested bullets with empty bold emphasis: `*   ****` or `*   ____`
*
* @param s - The input string to process
* @returns The string with malformed bullet patterns removed, or original if no matches
*/
function stripBullets(s) {
	const st = s.trimEnd();
	if (st.endsWith("\n    *") || st.endsWith("\n    -")) return s.slice(0, -5);
	if (st.endsWith("\n    *   **") || st.endsWith("\n    *   __") || st.endsWith("\n    -   **") || st.endsWith("\n    -   __")) return s.slice(0, -6);
	if (st.endsWith("*   ****") || st.endsWith("*   ____") || st.endsWith("-   ****") || st.endsWith("-   ____")) return s.slice(0, -8);
	return s;
}
/**
* Remove trailing Markdown emphasis/strike delimiters from a string.
*
* Handles double-character delimiters (`**`, `__`, `~~`) in these cases:
* - 2 delimiters (`**`, `__`, `~~`): requires a leading space; removes the entire suffix.
* - 4 delimiters (`****`, `____`, `~~~~`):
*      - if preceded by a space, removes all 4 characters.
*      - if not preceded by a space, removes only the last 2 characters.
* - 6 delimiters (`******`, `______`, `~~~~~~`): requires a leading space; removes all 6 characters.
*
* Examples:
* ```ts
* stripTrailingDelims("hello **");       // "hello"
* stripTrailingDelims(" hello ****");    // " hello"    (space exists → remove all 4)
* stripTrailingDelims("hello****");      // "hello**"   (no space → remove last 2)
* stripTrailingDelims(" hello ******");  // " hello"    (space exists → remove all 6)
* stripTrailingDelims("hello**A**");     // "hello**A**" (unchanged)
* stripTrailingDelims("hello**A B****"); // "hello**A B****" (unchanged)
* ```
*
* @param s - The input string to process.
* @returns The string with any matching trailing delimiters removed according to the rules above.
*/
function stripTrailingDelims(s) {
	const afterBulletStrip = stripBullets(s);
	if (afterBulletStrip !== s) return afterBulletStrip;
	for (const delim of DOUBLE_DELIMS) for (let count = 3; count >= 1; count--) {
		const suffix = delim.repeat(count);
		if (s === suffix) return "";
		if (s.endsWith(" " + suffix) || s.endsWith("\n" + suffix)) {
			const out = trimEndExceptNewlines(s.slice(0, -suffix.length));
			const closeDelimPos = out.lastIndexOf(delim);
			const openDelimPos = closeDelimPos > -1 ? out.slice(0, closeDelimPos).indexOf(delim) : -1;
			if (count === 2 && closeDelimPos > -1 && openDelimPos === -1) {
				if (!NUMBER_CHAR_REGEX.test(out?.[closeDelimPos - 1] ?? "")) return out + delim;
			}
			return out;
		}
		if (count === 2 && s.endsWith(suffix)) return s.slice(0, -2);
	}
	if (s.endsWith(" ~") || s.endsWith("\n~")) return s.slice(0, -1);
	if (s.endsWith("~~~")) return s.slice(0, -1);
	return s;
}
/**
* Trim trailing spaces and tabs from a string while preserving newlines.
*
* Unlike String.prototype.trimEnd(), this function only removes spaces and tabs
* from the end of the string, stopping at the first newline character encountered
* when scanning from right to left.
*
* @param str - The input string to trim
* @returns The string with trailing spaces and tabs removed, but newlines preserved
*
* @example
* trimEndExceptNewlines("hello   ") // "hello"
* trimEndExceptNewlines("hello\n  ") // "hello\n"
* trimEndExceptNewlines("hello\n\n  ") // "hello\n\n"
*/
function trimEndExceptNewlines(str) {
	let end = str.length;
	while (end > 0 && (str[end - 1] === " " || str[end - 1] === "	")) end--;
	return str.slice(0, end);
}
/**
* Complete partial horizontal rule patterns at the end of content.
* Converts trailing single or double hyphens to a complete horizontal rule (---).
*
* @param s - The input string to process
* @returns The string with completed horizontal rule if it ends with --
*/
function completeHorizontalRule(s) {
	const trimmed = s.trimEnd();
	const whitespaceLength = s.length - trimmed.length;
	if (trimmed.endsWith("--")) return s.slice(0, -2 - whitespaceLength) + "---" + s.slice(s.length - whitespaceLength);
	return s;
}
/**
* Remove invalid trailing double backticks from inline code auto-completion.
*
* When auto-close adds a backtick to close inline code at the end of content,
* it can create an invalid pattern like "text ``" which isn't valid markdown.
* This function removes such trailing double backticks, but only if they appear
* inline (not at the start of a line where they might be part of a code block).
*
* @param s - The input string to process
* @returns The string with invalid trailing double backticks removed
*
* @example
* removeTrailingDoubleBackticks("text ``"); // "text "
* removeTrailingDoubleBackticks("\n ``"); // "\n ``" (preserved, might be code block)
* removeTrailingDoubleBackticks("inline ``"); // "inline "
* removeTrailingDoubleBackticks("``"); // "" (invalid at start)
*/
function removeTrailingDoubleBackticks(s) {
	if (s === "``") return "";
	if (s.endsWith(" ``")) {
		if (s.length >= 4 && s[s.length - 4] === "\n") return s;
		return s.slice(0, s.length - 2);
	}
	return s;
}
//#endregion
//#region src/use-balanced-markdown.ts
/**
* Re‑balances **only** the last paragraph each render (text after the most recent blank line) and leaves the earlier
* content untouched.
*
* There is no persistent parser state – each call starts
* from a fresh `StreamState`, making the logic trivial while still keeping the
* work bounded to O(length of last paragraph).
*
* Suitable when input is **append‑only** (streaming LLM answer).
* If earlier text can mutate, bump `resetKey` to trigger
* a one‑off full rebalance.
*/
function useBalancedMarkdown(content, { autoBalance = true, autoCloseCodeBlocks = true, resetKey } = {}) {
	return useMemo(() => {
		if (!autoBalance) return content;
		const lfBreak = content.lastIndexOf("\n\n");
		const crlfBreak = content.lastIndexOf("\r\n\r\n");
		let breakPos = -1;
		let breakLen = 0;
		if (crlfBreak > lfBreak) {
			breakPos = crlfBreak;
			breakLen = 4;
		} else if (lfBreak !== -1) {
			breakPos = lfBreak;
			breakLen = 2;
		}
		return (breakPos === -1 ? "" : content.slice(0, breakPos + breakLen)) + autoCloseStream(breakPos === -1 ? content : content.slice(breakPos + breakLen), initState(), {
			resetState: true,
			autoCloseCodeBlocks
		});
	}, [
		content,
		resetKey,
		autoBalance,
		autoCloseCodeBlocks
	]);
}
//#endregion
//#region src/copyable/internal.ts
function parseAttrs(attrSection) {
	let label;
	let editable = true;
	const labelMatch = /\blabel\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attrSection);
	if (labelMatch) label = labelMatch[1] ?? labelMatch[2] ?? labelMatch[3];
	const editableMatch = /\beditable\s*=\s*(?:"(true|false)"|'(true|false)'|(true|false))/i.exec(attrSection);
	if (editableMatch) editable = (editableMatch[1] ?? editableMatch[2] ?? editableMatch[3]) === "true";
	return {
		label,
		editable
	};
}
function pushMarkdown$1(segments, chunk) {
	if (chunk.length === 0) return;
	const last = segments[segments.length - 1];
	if (last?.type === "markdown") last.content += chunk;
	else segments.push({
		type: "markdown",
		content: chunk
	});
}
//#endregion
//#region src/copyable/extract-copyable-blocks.ts
/**
* Splits assistant/markdown content into ordered segments: normal markdown and
* `<copyable>...</copyable>` regions (complete or still streaming).
*/
const OPEN_PREFIX = "<copyable";
const WORD_CHAR_RE = /[A-Za-z0-9_]/;
const TAG_NAME_START_RE = /[A-Za-z/]/;
const SMART_DOUBLE_QUOTES_RE = /\u201C|\u201D/g;
const SMART_SINGLE_QUOTES_RE = /\u2018|\u2019/g;
const LEADING_LINE_BREAK_RE = /^(?:\r\n|\r|\n)/;
const TRAILING_LINE_BREAK_RE = /(?:\r\n|\r|\n)$/;
const SURROUNDING_LINE_BREAK_RE = /^(?:\r\n|\r|\n)|(?:\r\n|\r|\n)$/g;
function normalizeTagQuotes(value) {
	return value.replace(SMART_DOUBLE_QUOTES_RE, "\"").replace(SMART_SINGLE_QUOTES_RE, "'");
}
function normalizeCopyableMarkup(content) {
	return content.replace(/&lt;(\/?\s*copyable\b[\s\S]*?)&gt;/gi, (_, tagBody) => `<${normalizeTagQuotes(tagBody)}>`).replace(/<\s*(\/?)\s*copyable\b([^>]*)>/gi, (_, slash, attrs) => slash === "/" ? "</copyable>" : `<copyable${normalizeTagQuotes(attrs)}>`);
}
function parseOpenTag(content, idx) {
	if (!content.startsWith(OPEN_PREFIX, idx)) return { kind: "incomplete" };
	const gt = content.indexOf(">", idx);
	if (gt === -1) return { kind: "incomplete" };
	const attrsPart = content.slice(idx + 9, gt).trim();
	return {
		kind: "ok",
		end: gt + 1,
		...parseAttrs(attrsPart)
	};
}
function isBlockStart(content, idx) {
	if (idx === 0) return true;
	let cursor = idx - 1;
	while (cursor >= 0) {
		const char = content[cursor];
		if (char === "\n" || char === "\r") return true;
		if (char !== " " && char !== "	") return false;
		cursor -= 1;
	}
	return true;
}
const ORIGINAL_STREAMING_OPEN_RE = /(?:<|&lt;)copyable\b/gi;
/**
* Like `findStreamingOpenTagStart` but operates directly on the original
* source so the returned offset is correct regardless of how normalize
* shrinks bytes earlier in the string (e.g., when an orphan `&lt;/copyable&gt;`
* appears before the real streaming open).
*/
function findStreamingOpenTagStartInOriginal(content) {
	ORIGINAL_STREAMING_OPEN_RE.lastIndex = 0;
	let match;
	while ((match = ORIGINAL_STREAMING_OPEN_RE.exec(content)) !== null) {
		const idx = match.index;
		const afterPrefix = idx + match[0].length;
		const nextChar = content[afterPrefix];
		if (nextChar !== void 0 && WORD_CHAR_RE.test(nextChar)) continue;
		if (!isBlockStart(content, idx)) continue;
		if (content.indexOf(">", afterPrefix) === -1 && content.indexOf("&gt;", afterPrefix) === -1) continue;
		return idx;
	}
	return -1;
}
/**
* Hides a trailing unclosed `<...` at a block-start position from the visible
* markdown while the token is still streaming. Generalizes the copyable-specific
* partial-prefix detection: any tag-shaped fragment at the tail gets suppressed
* until it either completes (and the extractor takes over) or resolves to
* non-tag text (on the next streaming tick).
*/
function stripTrailingUnclosedTag(content) {
	const lastLt = content.lastIndexOf("<");
	if (lastLt === -1) return content;
	if (content.indexOf(">", lastLt) !== -1) return content;
	if (!isBlockStart(content, lastLt)) return content;
	const nextChar = content[lastLt + 1];
	if (nextChar !== void 0 && !TAG_NAME_START_RE.test(nextChar)) return content;
	return content.slice(0, lastLt);
}
const TRAILING_PARTIAL_COPYABLE_CLOSE_RE = /(?:<|&lt;)\/(?:c(?:o(?:p(?:y(?:a(?:b(?:l(?:e\s*)?)?)?)?)?)?)?)?$/i;
/**
* Hides a trailing partial `</copyable` close tag from a streaming
* copyable's body. Without this, mid-stream chunks ending in fragments
* like "</copyab" leak through to the renderer and get shown as text.
*/
function stripTrailingPartialCopyableClose(content) {
	return content.replace(TRAILING_PARTIAL_COPYABLE_CLOSE_RE, "");
}
/**
* Matches a complete `<copyable>...</copyable>` block in the ORIGINAL source.
*
* The `\1` backreference forces the close tag's escape level to match the
* open tag's (`<` pairs with `</`, `&lt;` pairs with `&lt;/`), so a stray
* escaped open in prose never pairs up with a real raw close later on.
*
* Body matching is plain lazy — a `<copyable>` mention inside a block body
* is treated as literal text, which is what the model usually emits when
* drafting documentation about copyables. The inverse case (an inline
* `<copyable>` in prose followed by a real block later in the message) is
* rare and accepted as a known limitation: the inline mention will be
* consumed into the lazy body.
*/
const ORIGINAL_COMPLETE_BLOCK_RE = /(<|&lt;)\s*copyable\b[\s\S]*?\1\s*\/\s*copyable\s*(?:>|&gt;)/gi;
/**
* Splits `content` into markdown and copyable segments. Streaming responses use
* `complete: false` when the opening `<copyable>` is present but `</copyable>`
* has not arrived.
*
* Parsing runs on the original source as the single source of truth: each
* complete block is matched in the source, then its raw substring is
* locally normalized just to extract attributes and inner content. This
* keeps `raw`, the markdown chunks between blocks, and the streaming tail
* byte-identical with the input, which is what lets the rebuild splice the
* exact source bytes back in for unedited blocks.
*/
function extractCopyableBlocks(content) {
	if (!content.includes(OPEN_PREFIX) && !content.includes("&lt;")) return [{
		type: "markdown",
		content
	}];
	const segments = [];
	let cursor = 0;
	let match;
	ORIGINAL_COMPLETE_BLOCK_RE.lastIndex = 0;
	while ((match = ORIGINAL_COMPLETE_BLOCK_RE.exec(content)) !== null) {
		const raw = match[0];
		const origStart = match.index;
		const origEnd = origStart + raw.length;
		if (!isBlockStart(content, origStart)) {
			ORIGINAL_COMPLETE_BLOCK_RE.lastIndex = origStart + 1;
			continue;
		}
		pushMarkdown$1(segments, content.slice(cursor, origStart));
		const parsedBlock = parseCompleteCopyableBlockBody(raw);
		if (parsedBlock) segments.push({
			type: "copyable",
			content: parsedBlock.innerContent.replace(SURROUNDING_LINE_BREAK_RE, ""),
			raw,
			label: parsedBlock.label,
			editable: parsedBlock.editable,
			complete: true
		});
		else pushMarkdown$1(segments, raw);
		cursor = origEnd;
	}
	const tail = content.slice(cursor);
	const tailOpenIdx = findStreamingOpenTagStartInOriginal(tail);
	if (tailOpenIdx === -1) pushMarkdown$1(segments, stripTrailingUnclosedTag(tail));
	else {
		pushMarkdown$1(segments, tail.slice(0, tailOpenIdx));
		const rawStreamingBlock = tail.slice(tailOpenIdx);
		const normalizedStreamingBlock = normalizeCopyableMarkup(rawStreamingBlock);
		const open = parseOpenTag(normalizedStreamingBlock, 0);
		if (open.kind === "ok") segments.push({
			type: "copyable",
			content: stripTrailingPartialCopyableClose(normalizedStreamingBlock.slice(open.end)).replace(LEADING_LINE_BREAK_RE, ""),
			raw: rawStreamingBlock,
			label: open.label,
			editable: open.editable,
			complete: false
		});
	}
	if (segments.length === 0) return [{
		type: "markdown",
		content: ""
	}];
	return segments;
}
function buildOpenTag(segment) {
	let tag = "<copyable";
	if (segment.label !== void 0) tag += ` label="${segment.label}"`;
	tag += ` editable="${segment.editable}"`;
	return tag + ">";
}
const CLOSE_TAG_RE = /(?:<|&lt;)\/\s*copyable\s*(?:>|&gt;)/gi;
/**
* Quote-aware extraction of attrs and inner content for a complete copyable
* block. Runs the same walker the rebuild path uses (`findOpenTagEnd`) so
* extract and rebuild agree on where the open tag ends, even when `>` or
* `&gt;` appear inside a quoted attribute value. Operates on the raw block
* directly because `normalizeCopyableMarkup` would mangle bytes when its
* own regex stops at the inner `>`. Smart quotes in the attrs section are
* normalized locally so `parseAttrs` can match the value. Returns null
* when the open tag or its matching close can't be located - callers fall
* through to the markdown fallback so source bytes aren't dropped.
*/
function parseCompleteCopyableBlockBody(rawBlock) {
	const openTagEnd = findOpenTagEnd(rawBlock);
	if (openTagEnd === -1) return null;
	CLOSE_TAG_RE.lastIndex = 0;
	let lastClose = null;
	let probe;
	while ((probe = CLOSE_TAG_RE.exec(rawBlock)) !== null) lastClose = probe;
	if (!lastClose || lastClose.index < openTagEnd) return null;
	const prefixLen = rawBlock.startsWith("&lt;") ? 12 : 9;
	const terminatorLen = rawBlock.slice(openTagEnd - 4, openTagEnd).endsWith("&gt;") ? 4 : 1;
	const parsed = parseAttrs(normalizeTagQuotes(rawBlock.slice(prefixLen, openTagEnd - terminatorLen)).trim());
	return {
		label: parsed.label,
		editable: parsed.editable,
		innerContent: normalizeCopyableMarkup(rawBlock.slice(openTagEnd, lastClose.index))
	};
}
/**
* Either-direction smart quotes are accepted as openers because LLMs (and
* autocorrect) occasionally emit inverted pairs like `\u201Dvalue\u201C`. The closer
* is the counterpart code point so the walker exits on whichever direction
* actually appears next inside the attribute.
*/
const QUOTE_CLOSERS = {
	"\"": "\"",
	"'": "'",
	"“": "”",
	"”": "“",
	"‘": "’",
	"’": "‘"
};
/**
* Returns the position right after the open tag terminator, walking with
* quote awareness so `>` or `&gt;` inside an attribute value doesn't get
* mistaken for the end of the tag. Recognizes straight and smart quotes.
*/
function findOpenTagEnd(raw) {
	let i;
	if (raw.startsWith("&lt;")) i = 4;
	else if (raw.startsWith("<")) i = 1;
	else return -1;
	let closer = null;
	while (i < raw.length) {
		const ch = raw[i];
		if (closer) {
			if (ch === closer) closer = null;
			i++;
			continue;
		}
		const maybeCloser = QUOTE_CLOSERS[ch];
		if (maybeCloser !== void 0) {
			closer = maybeCloser;
			i++;
			continue;
		}
		if (ch === ">") return i + 1;
		if (raw.startsWith("&gt;", i)) return i + 4;
		i++;
	}
	return -1;
}
/** Slot `newInner` into `raw`, keeping the original tags (escaped or raw) and the newlines just inside them. */
function substituteCopyableInner(segment, newInner) {
	const openTagEnd = findOpenTagEnd(segment.raw);
	CLOSE_TAG_RE.lastIndex = 0;
	let lastCloseMatch = null;
	let closeProbe;
	while ((closeProbe = CLOSE_TAG_RE.exec(segment.raw)) !== null) lastCloseMatch = closeProbe;
	if (openTagEnd === -1 || !lastCloseMatch || lastCloseMatch.index < openTagEnd) return `${buildOpenTag(segment)}${newInner}</copyable>`;
	const openTag = segment.raw.slice(0, openTagEnd);
	const innerSlice = segment.raw.slice(openTagEnd, lastCloseMatch.index);
	const closeTag = segment.raw.slice(lastCloseMatch.index);
	return `${openTag}${LEADING_LINE_BREAK_RE.exec(innerSlice)?.[0] ?? ""}${newInner}${TRAILING_LINE_BREAK_RE.exec(innerSlice)?.[0] ?? ""}${closeTag}`;
}
/**
* Rebuild the full markdown after a user edits one copyable block. Unedited
* blocks are kept byte-for-byte so editing one does not normalize the others
* and bust the prompt cache.
*/
function rebuildWithCopyableEdit(content, segmentIndex, newText) {
	return extractCopyableBlocks(content).map((segment, i) => {
		if (segment.type === "markdown") return segment.content;
		if (i !== segmentIndex) return segment.raw;
		return substituteCopyableInner(segment, newText);
	}).join("");
}
//#endregion
//#region src/strip-markdown.ts
const COPYABLE_OPENING_TAG_WITH_ATTRS_RE = /<copyable\b(?=[^>]*\s+\w+=)[^>]*>/gi;
const COPYABLE_CLOSING_TAG_RE = /<\/copyable\s*>/gi;
/**
* Removes Markdown formatting from text while preserving the content.
* Strips images, links, formatting symbols, headers, blockquotes, and lists.
*
* @param text - The Markdown text to strip
* @returns Plain text with Markdown formatting removed
*
* @example
* stripMarkdown("# Hello **world**")
* // Returns: "Hello world"
*/
function stripMarkdown(text) {
	return extractCopyableBlocks(text).map((segment) => segment.content).join("").replace(COPYABLE_OPENING_TAG_WITH_ATTRS_RE, "").replace(COPYABLE_CLOSING_TAG_RE, "").replace(/!\[.*?\]\(.*?\)/g, "").replace(/\[([^\]]+)\]\((.*?)\)/g, "$1").replace(/[*_~`]+/g, "").replace(/^#{1,6}\s*/gm, "").replace(/^>\s?/gm, "").replace(/^\s*[-*+]\s+/gm, "").replace(/^\s*(\d+)\.\s+/gm, "$1. ").replace(/^(-{3,}|\*{3,})$/gm, "").trim();
}
/**
* Projects Markdown onto a single line of plain prose.
*
* `stripMarkdown` removes the syntax but keeps the block structure, so its
* output still carries the newlines that separated headings, list items, and
* paragraphs. A single-line projection is what callers need when the text has
* to be measured or line-clamped — a wrapped `Text` cannot tell the difference
* between a paragraph break and a wrap, and an excerpt that keeps them ends up
* clamped after one short heading.
*
* @param text - The Markdown text to project
* @returns One line of plain text, whitespace runs collapsed to single spaces
*
* @example
* markdownToPlainText("# Overview\n\n- First **point**\n- Second point")
* // Returns: "Overview First point Second point"
*/
function markdownToPlainText(text) {
	return stripMarkdown(text).replace(/\s+/g, " ").trim();
}
//#endregion
//#region src/markdown-excerpt.ts
const marked$3 = new Marked({ gfm: true });
const MARKUP_TOKENS = /* @__PURE__ */ new Set([
	"code",
	"hr",
	"space",
	"def",
	"html"
]);
const FRONT_MATTER_RE = /^---[ \t]*\r?\n[A-Za-z_][\w.-]*[ \t]*:[\s\S]*?\r?\n(?:---|\.\.\.)[ \t]*(?:\r?\n|$)/;
const flattenTable = (table) => [table.header, ...table.rows].flatMap((row) => row.map((cell) => markdownToPlainText(cell.text))).filter((cell) => cell.length > 0).join(" ");
/**
* One line of the prose a Markdown document actually says, for an excerpt that
* has to stand in for the whole thing.
*
* `markdownToPlainText` keeps whatever the text leads with, so a document
* opening with a table, a code block or an image excerpts as pipes, source
* code, or nothing at all. This reads only the blocks carrying prose.
*
* Returns `''` when there is none, leaving the caller its own fallback.
*
* @example
* markdownToExcerpt('<canvas artifact presented above />')
* // Returns: ""
* markdownToExcerpt('| Metric | Value |\n|---|---|\n| Revenue | 4% |')
* // Returns: "Metric Value Revenue 4%"
*/
function markdownToExcerpt(markdown) {
	const source = FRONT_MATTER_RE.test(markdown.trim()) ? markdown.trim().replace(FRONT_MATTER_RE, "") : markdown.trim();
	if (source.length === 0) return "";
	const prose = [];
	const tables = [];
	for (const token of marked$3.lexer(source)) {
		if (MARKUP_TOKENS.has(token.type)) continue;
		if (token.type === "table") {
			tables.push(flattenTable(token));
			continue;
		}
		const text = markdownToPlainText(token.raw);
		if (text.length > 0) prose.push(text);
	}
	if (prose.length > 0) return prose.join(" ");
	return tables.join(" ").trim();
}
//#endregion
//#region src/markdown-preview.ts
const PREVIEW_TOKEN_TYPES = /* @__PURE__ */ new Set([
	"paragraph",
	"blockquote",
	"list"
]);
const getMarkdownPreview = (markdown) => {
	const tokens = marked.lexer(markdown);
	const firstIndex = tokens.findIndex(({ type }) => type !== "space");
	const first = tokens[firstIndex];
	if (!first) return "";
	if (PREVIEW_TOKEN_TYPES.has(first.type)) return first.raw;
	if (first.type === "heading") for (const token of tokens.slice(firstIndex + 1)) {
		if (token.type === "heading") break;
		if (PREVIEW_TOKEN_TYPES.has(token.type)) return token.raw;
	}
	return markdownToPlainText((first.type === "table" ? [...first.header, ...first.rows.flat()].map((cell) => cell.text).join(" ") : "text" in first && typeof first.text === "string" ? first.text : first.raw).replace(/<[^>]*>/g, " "));
};
//#endregion
//#region src/markdown-html.ts
const marked$2 = new Marked({ gfm: true });
/**
* Markdown as HTML, for surfaces that draw HTML already and would otherwise need a
* second renderer. Synchronous: `marked.parse` returns a promise only when an
* async extension is registered, and none is.
*
* @example
* markdownToHtml('## Changes\nOne **reminder** per event.')
* // Returns: "<h2>Changes</h2>\n<p>One <strong>reminder</strong> per event.</p>\n"
*/
const markdownToHtml = (markdown) => marked$2.parse(markdown, { async: false });
const escapeHtml = (text) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const edited = new Marked({
	gfm: true,
	breaks: true,
	renderer: {
		html: ({ text, block }) => block ? `<p>${escapeHtml(text.trim())}</p>\n` : escapeHtml(text),
		image: ({ raw }) => escapeHtml(raw)
	},
	tokenizer: {
		autolink: () => void 0,
		codespan: () => void 0,
		code: () => void 0,
		fences: () => void 0,
		hr: () => void 0,
		table: () => void 0
	}
});
/** Markdown typed into a rich editor, as HTML: each line stays a line, tags stay text. */
const editedMarkdownToHtml = (markdown) => edited.parse(markdown, { async: false });
//#endregion
//#region src/copyable/recover-copyable-blocks.ts
/**
* Fallback recovery for <copyable> blocks that the canonical parser missed.
* Pure string transformation — no React dependencies.
*/
const COMPLETE_BLOCK_RE = /(^|\n)([ \t]*)<copyable\b([^>]*)>([\s\S]*?)<\/copyable\s*>/g;
/**
* Attempts to recover copyable blocks from raw content using a regex-based
* fallback. Returns segments if any blocks are found, or `null` if none match.
*
* Recovered blocks are always marked `editable: false` as a safety net —
* the canonical parser failed to recognize them in the source text.
*/
function recoverCopyableBlocksForRendering(content) {
	const segments = [];
	let cursor = 0;
	let match;
	COMPLETE_BLOCK_RE.lastIndex = 0;
	while ((match = COMPLETE_BLOCK_RE.exec(content)) !== null) {
		const fullMatch = match[0];
		const linePrefix = match[1] ?? "";
		const indent = match[2] ?? "";
		const attrsPart = match[3] ?? "";
		const innerContent = match[4] ?? "";
		const matchStart = match.index;
		const blockStart = matchStart + linePrefix.length;
		pushMarkdown$1(segments, content.slice(cursor, blockStart));
		const parsed = parseAttrs(attrsPart);
		segments.push({
			type: "copyable",
			content: innerContent,
			raw: content.slice(blockStart, matchStart + fullMatch.length),
			label: parsed.label,
			editable: false,
			complete: true
		});
		cursor = matchStart + fullMatch.length;
		if (indent.length > 0) pushMarkdown$1(segments, linePrefix);
	}
	if (segments.length === 0) return null;
	pushMarkdown$1(segments, content.slice(cursor));
	return segments;
}
/**
* Applies recovery across an array of segments, scanning markdown segments
* for missed copyable blocks. Returns the updated segments and a count of
* how many blocks were recovered.
*/
function recoverCopyableBlocksInSegments(segments) {
	const recoveredSegments = [];
	let recoveredCopyableCount = 0;
	for (const segment of segments) {
		if (segment.type === "copyable") {
			recoveredSegments.push(segment);
			continue;
		}
		const recoveredMarkdownSegments = recoverCopyableBlocksForRendering(segment.content);
		if (!recoveredMarkdownSegments) {
			pushMarkdown$1(recoveredSegments, segment.content);
			continue;
		}
		recoveredCopyableCount += recoveredMarkdownSegments.filter((recoveredSegment) => recoveredSegment.type === "copyable").length;
		for (const recoveredSegment of recoveredMarkdownSegments) {
			if (recoveredSegment.type === "markdown") {
				pushMarkdown$1(recoveredSegments, recoveredSegment.content);
				continue;
			}
			recoveredSegments.push(recoveredSegment);
		}
	}
	return {
		segments: recoveredSegments,
		recoveredCopyableCount
	};
}
//#endregion
//#region src/copyable/detect-copyable-parse-failure.ts
/**
* Detects when the copyable block parser fails to recognize a block-level
* <copyable> tag that is present in the content. Pure content analysis —
* no app-specific context (threadId, messageId, etc.).
*/
const BLOCK_COPYABLE_TAG_RE = /(^|\n)[ \t]*<copyable\b[^>]*>/i;
function findBlockCopyableTagIndex(content) {
	const match = BLOCK_COPYABLE_TAG_RE.exec(content);
	if (!match) return -1;
	return match.index + match[1].length;
}
/**
* Analyzes content at each pipeline stage to detect whether a block-level
* `<copyable>` tag exists but fails to be parsed by `extractCopyableBlocks`.
*
* Returns diagnostics if a parse failure is detected, or `null` if no failure.
*/
function detectCopyableParseFailure(opts) {
	const { rawContent, smoothedContent, balancedContent } = opts;
	const balancedTagIndex = findBlockCopyableTagIndex(balancedContent);
	if (balancedTagIndex === -1) return null;
	if (!extractCopyableBlocks(balancedContent).some((segment) => segment.type === "markdown" && findBlockCopyableTagIndex(segment.content) !== -1)) return null;
	const rawTagIndex = findBlockCopyableTagIndex(rawContent);
	const smoothedTagIndex = findBlockCopyableTagIndex(smoothedContent);
	return {
		rawHasBlockCopyableTag: rawTagIndex !== -1,
		smoothedHasBlockCopyableTag: smoothedTagIndex !== -1,
		balancedHasBlockCopyableTag: true,
		rawLength: rawContent.length,
		smoothedLength: smoothedContent.length,
		balancedLength: balancedContent.length,
		rawTagIndex,
		smoothedTagIndex,
		balancedTagIndex
	};
}
//#endregion
//#region src/code-blocks/extract-code-blocks.ts
const OPEN_FENCE_RE = /^(`{3,})([^`]*)$/;
const CLOSE_FENCE_RE = /^(`{3,})[ \t]*$/;
function pushMarkdown(segments, chunk) {
	if (chunk.length === 0) return;
	const last = segments[segments.length - 1];
	if (last?.type === "markdown") last.content += chunk;
	else segments.push({
		type: "markdown",
		content: chunk
	});
}
function extractCodeBlocks(content) {
	if (content.length === 0) return [{
		type: "markdown",
		content: ""
	}];
	const lines = content.replace(/\r\n?/g, "\n").split("\n");
	const segments = [];
	let buffer = [];
	const flushMarkdown = () => {
		if (buffer.length === 0) return;
		pushMarkdown(segments, buffer.join("\n"));
		buffer = [];
	};
	let i = 0;
	while (i < lines.length) {
		const openMatch = OPEN_FENCE_RE.exec(lines[i]);
		if (!openMatch) {
			buffer.push(lines[i]);
			i += 1;
			continue;
		}
		flushMarkdown();
		const fenceLength = openMatch[1].length;
		const language = openMatch[2].trim() || void 0;
		const codeLines = [];
		let j = i + 1;
		let foundClose = false;
		while (j < lines.length) {
			const closeMatch = CLOSE_FENCE_RE.exec(lines[j]);
			if (closeMatch && closeMatch[1].length >= fenceLength) {
				foundClose = true;
				break;
			}
			codeLines.push(lines[j]);
			j += 1;
		}
		segments.push({
			type: "code",
			content: codeLines.join("\n"),
			language,
			complete: foundClose
		});
		if (foundClose) i = j + 1;
		else i = lines.length;
	}
	flushMarkdown();
	if (segments.length === 0) return [{
		type: "markdown",
		content: ""
	}];
	return segments;
}
//#endregion
//#region src/extract-markdown-link-spans.ts
const marked$1 = new Marked({ gfm: true });
/**
* Every markdown `link` token with its position in the source. Code spans and
* fenced blocks produce no link tokens, so urls written inside them stay text.
* A link is only reported once its closing `)` has arrived, which keeps
* streamed markdown from matching half a link.
*/
function extractMarkdownLinkSpans(markdown) {
	const spans = [];
	let searchFrom = 0;
	marked$1.walkTokens(marked$1.lexer(markdown), (token) => {
		if (token.type !== "link") return;
		const start = markdown.indexOf(token.raw, searchFrom);
		if (start === -1) return;
		searchFrom = start + token.raw.length;
		spans.push({
			href: token.href,
			raw: token.raw,
			text: token.text,
			start,
			end: searchFrom
		});
	});
	return spans;
}
//#endregion
//#region src/ask-user/strip-ask-user-blocks.ts
const ASK_USER_BLOCK_PATTERN = /\n*<!--lb:(questions|answers)-->[\s\S]*?<!--\/lb:\1-->\n*/g;
function stripAskUserBlocks(content) {
	if (!content.includes("<!--lb:")) return content;
	return content.replace(ASK_USER_BLOCK_PATTERN, "\n\n").trim();
}
//#endregion
//#region src/notes.ts
const MAX_DEPTH = 20;
function serializeImagePlaceholder(message) {
	return `*${message}*\n\n`;
}
function notesToText(node, depth = 0) {
	if (!node || depth > MAX_DEPTH) return "";
	if (node.text) return node.text;
	if (!node.content) return "";
	const children = node.content.map((child) => notesToText(child, depth + 1));
	const isBlockContainer = node.type === "doc" || node.type === "bulletList" || node.type === "orderedList" || node.type === "blockquote";
	return children.join(isBlockContainer ? "\n" : "");
}
function escapeMarkdownText(text) {
	return text.replace(/\\/g, "\\\\").replace(/([`*_{}\[\]()#+.!|>~-])/g, "\\$1");
}
function escapeMarkdownUrl(url) {
	return url.replace(/\\/g, "\\\\").replace(/\)/g, "\\)");
}
function wrapCodeSpan(text) {
	const backtickRuns = text.match(/`+/g) ?? [];
	const longestRun = Math.max(0, ...backtickRuns.map((run) => run.length));
	const fence = "`".repeat(longestRun + 1);
	return `${fence}${text.startsWith("`") || text.endsWith("`") ? ` ${text} ` : text}${fence}`;
}
function wrapInlineMark(text, wrap) {
	const match = text.match(/^(\s*)([\s\S]*?)(\s*)$/);
	if (!match) return wrap(text);
	const [, leadingWhitespace, innerText, trailingWhitespace] = match;
	if (!innerText) return text;
	return `${leadingWhitespace}${wrap(innerText)}${trailingWhitespace}`;
}
function applyMarks(text, marks) {
	if (!marks?.length) return escapeMarkdownText(text);
	return marks.reduce((value, mark) => {
		switch (mark.type) {
			case "bold":
			case "strong": return wrapInlineMark(value, (innerText) => `**${innerText}**`);
			case "italic":
			case "em": return wrapInlineMark(value, (innerText) => `*${innerText}*`);
			case "underline": return wrapInlineMark(value, (innerText) => `__${innerText}__`);
			case "strike":
			case "strikethrough":
			case "strikeThrough": return wrapInlineMark(value, (innerText) => `~~${innerText}~~`);
			case "code": return wrapInlineMark(text, (innerText) => wrapCodeSpan(innerText));
			case "link": {
				const href = typeof mark.attrs?.href === "string" ? escapeMarkdownUrl(mark.attrs.href) : "";
				return href ? wrapInlineMark(value, (innerText) => `[${innerText}](${href})`) : value;
			}
			default: return value;
		}
	}, escapeMarkdownText(text));
}
function trimTrailingNewlines(value) {
	return value.replace(/\n+$/, "");
}
function indentBlock(value) {
	return value.split("\n").map((line) => line ? `  ${line}` : line).join("\n");
}
function serializeChildren(node, depth, options) {
	return (node?.content ?? []).map((child) => serializeNode(child, depth + 1, options)).join("");
}
function serializeListItem(node, prefix, depth, options) {
	const blocks = (node.content ?? []).map((child) => trimTrailingNewlines(serializeNode(child, depth + 1, options))).filter(Boolean);
	if (blocks.length === 0) return prefix;
	const [firstBlock, ...restBlocks] = blocks;
	return [`${prefix} ${firstBlock}`, ...restBlocks.map((block) => indentBlock(block))].join("\n");
}
function serializeList(node, ordered, depth, options) {
	const start = ordered && typeof node.attrs?.start === "number" ? node.attrs.start : 1;
	return (node.content ?? []).map((child, index) => serializeListItem(child, ordered ? `${start + index}.` : "-", depth, options)).join("\n");
}
function serializeTaskList(node, depth, options) {
	return (node.content ?? []).map((child) => serializeListItem(child, child.attrs?.checked === true ? "- [x]" : "- [ ]", depth, options)).join("\n");
}
function serializeNode(node, depth, options) {
	if (!node || depth > MAX_DEPTH) return "";
	switch (node.type) {
		case "doc": return trimTrailingNewlines(serializeChildren(node, depth, options));
		case "text": return applyMarks(node.text ?? "", node.marks);
		case "hardBreak": return "  \n";
		case "paragraph": {
			const content = serializeChildren(node, depth, options);
			return content ? `${content}\n\n` : "\n\n";
		}
		case "heading": {
			const level = typeof node.attrs?.level === "number" ? Math.min(Math.max(node.attrs.level, 1), 6) : 1;
			const content = serializeChildren(node, depth, options);
			return `${"#".repeat(level)} ${content}\n\n`;
		}
		case "bulletList": return `${serializeList(node, false, depth, options)}\n\n`;
		case "orderedList": return `${serializeList(node, true, depth, options)}\n\n`;
		case "taskList": return `${serializeTaskList(node, depth, options)}\n\n`;
		case "blockquote": {
			const content = trimTrailingNewlines(serializeChildren(node, depth, options));
			if (!content) return "";
			return content.split("\n").map((line) => line ? `> ${line}` : ">").join("\n") + "\n\n";
		}
		case "codeBlock": return `\`\`\`\n${trimTrailingNewlines(notesToText(node, depth))}\n\`\`\`\n\n`;
		case "emptyLine": {
			const content = trimTrailingNewlines(serializeChildren(node, depth, options));
			return content ? `${content}\n\n` : "\n\n";
		}
		case "horizontalRule": return "---\n\n";
		case "image": {
			const alt = typeof node.attrs?.alt === "string" && node.attrs.alt.trim() ? escapeMarkdownText(node.attrs.alt.trim()) : "Image";
			const fileId = typeof node.attrs?.fileId === "string" ? node.attrs.fileId : "";
			const src = fileId ? options.imageUrlsByFileId?.[fileId] : "";
			const isLoading = fileId ? options.loadingImageFileIds?.has(fileId) === true : false;
			if (src) return `![${alt}](${escapeMarkdownUrl(src)})\n\n`;
			return isLoading ? serializeImagePlaceholder("Loading image...") : serializeImagePlaceholder("Image unavailable");
		}
		default:
			if (node.text) return applyMarks(node.text, node.marks);
			return serializeChildren(node, depth, options);
	}
}
function notesToMarkdown(node, depth = 0, options = {}) {
	return serializeNode(node, depth, options).trim();
}
function collectImageFileIds(node, fileIds = /* @__PURE__ */ new Set()) {
	if (!node || typeof node !== "object") return Array.from(fileIds);
	if (node.type === "image" && typeof node.attrs?.fileId === "string") fileIds.add(node.attrs.fileId);
	for (const child of node.content ?? []) collectImageFileIds(child, fileIds);
	return Array.from(fileIds);
}
function filterResolvedImageUrls(imageUrlsByFileId, fileIds) {
	if (fileIds.length === 0) return {};
	const nextFileIds = new Set(fileIds);
	return Object.fromEntries(Object.entries(imageUrlsByFileId).filter(([fileId]) => nextFileIds.has(fileId)));
}
function areImageUrlMapsEqual(left, right) {
	const leftKeys = Object.keys(left);
	const rightKeys = Object.keys(right);
	if (leftKeys.length !== rightKeys.length) return false;
	return leftKeys.every((key) => left[key] === right[key]);
}
function textToNotes(text) {
	return {
		type: "doc",
		content: text.split("\n").map((line) => ({
			type: "paragraph",
			...line ? { content: [{
				type: "text",
				text: line
			}] } : {}
		}))
	};
}
//#endregion
//#region src/routines/strip-routine-preamble.ts
const ROUTINE_PREAMBLE_PATTERN = /<routine-preamble>[\s\S]*?<\/routine-preamble>\s*/g;
function stripRoutinePreamble(content) {
	if (!content.includes("<routine-preamble>")) return content;
	return content.replace(ROUTINE_PREAMBLE_PATTERN, "");
}
//#endregion
//#region src/meeting-intent/strip-meeting-intent-prompt.ts
const MEETING_INTENT_PROMPT_PATTERN = /<meeting-intent-prompt>[\s\S]*?<\/meeting-intent-prompt>\s*/g;
function stripMeetingIntentPrompt(content) {
	if (!content.includes("<meeting-intent-prompt>")) return content;
	return content.replace(MEETING_INTENT_PROMPT_PATTERN, "");
}
//#endregion
//#region src/entity-reference.ts
const ENTITY_REFERENCE_PATTERN = /::([a-z][\w-]*)\{([^{}\r\n]*)\}/;
const ENTITY_REFERENCE_START_PATTERN = new RegExp(`^${ENTITY_REFERENCE_PATTERN.source}`);
const parseEntityReference = (src) => {
	const match = ENTITY_REFERENCE_START_PATTERN.exec(src);
	if (!match) return void 0;
	const attributes = {};
	for (const argument of match[2].split(",")) {
		const attribute = /^\s*([a-z][\w-]*)\s*=\s*(.*?)\s*$/.exec(argument);
		if (!attribute) return void 0;
		attributes[attribute[1]] = attribute[2];
	}
	return {
		raw: match[0],
		entity: match[1],
		attributes
	};
};
//#endregion
//#region src/msg-channels/strip-msg-channel-tags.ts
const MSG_CHANNEL_TAG_PATTERN = /<\/?(?:lb_user_msg|lb_quoted_msg|lb_msg_channel_system|lb_forwarded_mail)\b[^>]*>/gi;
const XML_ENTITIES = [
	[/&lt;/g, "<"],
	[/&gt;/g, ">"],
	[/&amp;/g, "&"]
];
function stripMsgChannelTags(content) {
	if (!content.includes("<lb_")) return content;
	const withoutTags = content.replace(MSG_CHANNEL_TAG_PATTERN, "");
	return XML_ENTITIES.reduce((text, [entity, character]) => text.replace(entity, character), withoutTags);
}
//#endregion
export { ENTITY_REFERENCE_PATTERN, areImageUrlMapsEqual, autoCloseStream, collectImageFileIds, completeTrailingLinks, detectCopyableParseFailure, editedMarkdownToHtml, extractCodeBlocks, extractCopyableBlocks, extractMarkdownLinkSpans, filterResolvedImageUrls, getMarkdownPreview, initState, markdownToExcerpt, markdownToHtml, markdownToPlainText, notesToMarkdown, notesToText, parseEntityReference, rebuildWithCopyableEdit, recoverCopyableBlocksForRendering, recoverCopyableBlocksInSegments, stripAskUserBlocks, stripMarkdown, stripMeetingIntentPrompt, stripMsgChannelTags, stripRoutinePreamble, stripTrailingDelims, textToNotes, useBalancedMarkdown };

//# sourceMappingURL=index.js.map